import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, ChevronDown, Check, Settings, Building2, BarChart3, Users } from "lucide-react";
import { useVenue } from "@/context/VenueContext";
import api from "@/lib/api";

export function VenueSwitcher() {
  const { venues, groups, activeVenue, currentVenue, setActiveVenue, role } = useVenue();
  const [open, setOpen] = useState(false);
  const [presence, setPresence] = useState({});
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (!venues || venues.length === 0) return;
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/plus/venues/presence");
        if (alive) setPresence(Object.fromEntries((data || []).map((r) => [r.id, r.presenti])));
      } catch { /* noop */ }
    };
    load();
    const t = setInterval(load, 30000);
    const onVenue = () => load();
    window.addEventListener("venue-changed", onVenue);
    return () => { alive = false; clearInterval(t); window.removeEventListener("venue-changed", onVenue); };
  }, [venues]);

  if (!venues || venues.length === 0) return null;
  const canManage = role === "GROUP_OWNER" || role === "PLATFORM_OWNER";
  const curPresent = presence[activeVenue];

  return (
    <div className="relative" ref={ref}>
      <button data-testid="venue-switcher-btn" onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-zinc-900 border border-zinc-800 hover:border-sky-500/50 text-sm">
        <MapPin className="w-4 h-4 text-sky-400" />
        <span className="max-w-[150px] truncate font-medium">{currentVenue?.nome || "Seleziona sala"}</span>
        {curPresent > 0 && (
          <span data-testid="venue-btn-presence" className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-[11px] font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />{curPresent}
          </span>
        )}
        <ChevronDown className="w-4 h-4 text-zinc-500" />
      </button>
      {open && (
        <div data-testid="venue-switcher-menu" className="absolute right-0 mt-2 w-64 bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">Rete sale</div>
          <div className="max-h-80 overflow-auto py-1">
            {(groups || []).map((g) => {
              const gv = venues.filter((v) => v.group_id === g.id);
              if (gv.length === 0) return null;
              return (
                <div key={g.id} className="mb-1">
                  <button data-testid={`venue-group-${g.id}`}
                    onClick={() => { setOpen(false); navigate(`/cockpit/${g.id}`); }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-zinc-800/70 group">
                    <Building2 className="w-3.5 h-3.5 text-amber-400" />
                    <span className="flex-1 truncate text-[11px] font-semibold uppercase tracking-wide text-amber-300/90">{g.nome}</span>
                    <span className="text-[10px] text-sky-400 opacity-0 group-hover:opacity-100">Cockpit ›</span>
                  </button>
                  {gv.map((v) => (
                    <button key={v.id} data-testid={`venue-option-${v.id}`}
                      onClick={() => { setActiveVenue(v.id); setOpen(false); }}
                      className="w-full flex items-center gap-2 pl-7 pr-3 py-2 text-sm hover:bg-zinc-800 text-left border-l border-zinc-800 ml-3">
                      <span className={`w-1.5 h-1.5 rounded-full ${v.stato === "ACTIVE" ? "bg-emerald-400" : "bg-red-500"}`} />
                      <span className="flex-1 truncate">{v.nome}</span>
                      {presence[v.id] > 0 && (
                        <span data-testid={`venue-presence-${v.id}`} className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-[11px] font-semibold">
                          <Users className="w-3 h-3" />{presence[v.id]}
                        </span>
                      )}
                      {v.id === activeVenue && <Check className="w-4 h-4 text-sky-400" />}
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
          {canManage && (
            <button data-testid="venue-dashboard-link" onClick={() => { setOpen(false); navigate("/dashboard"); }}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm border-t border-zinc-800 text-emerald-400 hover:bg-zinc-800">
              <BarChart3 className="w-4 h-4" /> Confronto sale
            </button>
          )}
          {canManage && (
            <button data-testid="venue-manage-link" onClick={() => { setOpen(false); navigate("/manage"); }}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm border-t border-zinc-800 text-sky-400 hover:bg-zinc-800">
              <Settings className="w-4 h-4" /> Gestione sale &amp; staff
            </button>
          )}
          {role === "PLATFORM_OWNER" && (
            <button data-testid="venue-platform-link" onClick={() => { setOpen(false); navigate("/platform"); }}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm border-t border-zinc-800 text-amber-400 hover:bg-zinc-800">
              <Building2 className="w-4 h-4" /> Console Platform
            </button>
          )}
        </div>
      )}
    </div>
  );
}
